<template>
  <div class="min-h-screen bg-light-screen-background dark:bg-dark-screen-background">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header Section -->
      <div class="mb-8">
        <div class="flex items-center gap-3 mb-2">
          <div class="flex items-center justify-center w-10 h-10 rounded-lg bg-light-brand-blue dark:bg-dark-brand-blue">
            <LockKeyhole class="w-5 h-5 text-white" />
          </div>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-dark-body-text">
            {{ $t("Update Your Password") }}
          </h1>
        </div>
        <p class="text-lg text-gray-600 dark:text-gray-400 ml-13">
          {{ $t("Ensure your account is protected with a strong and unique password.") }}
        </p>
      </div>

      <SettingsCard :name="$t('Change Password')" :description="$t('Choose a strong password and don\'t reuse it for other accounts.')">
        <Form :validation-schema="schema" class="space-y-6" @submit="onSubmit">
          <div class="grid grid-cols-1 gap-y-6 p-6">
            <div>
              <TextInput
                name="old-password"
                type="password"
                :label="$t('Current Password')"
                :placeholder="$t('Enter your current password')"
                input-classes="appearance-none block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                label-classes="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" />
            </div>

            <div>
              <TextInput
                name="password"
                type="password"
                :label="$t('New Password')"
                :placeholder="$t('Enter your new password')"
                input-classes="appearance-none block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                label-classes="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" />
            </div>

            <div>
              <TextInput
                name="re-password"
                type="password"
                :label="$t('Confirm New Password')"
                :placeholder="$t('Re-type your new password')"
                input-classes="appearance-none block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                label-classes="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" />
            </div>
          </div>
          <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
            <button type="submit" class="btn primary-btn w-fit">
              <Save class="w-5 h-5 mr-2" />
              {{ $t("Update Password") }}
            </button>
          </div>
        </Form>
      </SettingsCard>
    </div>
  </div>
</template>

<script>
import AuthService from "@/services/auth.service"
import * as Yup from "yup"
import { Form } from "vee-validate"
import TextInput from "@/components/shared/TextInput.vue"
import SettingsCard from "@/components/shared/SettingsCard.vue"
import { LockKeyhole, Save } from "lucide-vue-next"

export default {
  name: "PasswordReset",

  components: {
    TextInput,
    Form,
    SettingsCard,
    LockKeyhole,
    Save
  },

  emits: ["changeSettingsTab"],
  setup() {
    function equalTo(ref, msg) {
      // eslint-disable-next-line vue/no-undef-properties
      return this.test({
        name: "equalTo",
        exclusive: false,
        message: msg || "${path} must be the same as ${reference}",
        params: {
          reference: ref.path
        },
        test: function (value) {
          // eslint-disable-next-line vue/no-undef-properties
          return value === this.resolve(ref)
        }
      })
    }

    Yup.addMethod(Yup.string, "equalTo", equalTo)
    const schema = Yup.object().shape({
      "old-password": Yup.string().min(8).required(),
      password: Yup.string().min(8).required(),
      "re-password": Yup.string().equalTo(Yup.ref("password"))
    })

    return {
      schema
    }
  },

  data() {
    return {
      display_done_form: false,
      display_error_form: false
    }
  },

  methods: {
    onSubmit(values) {
      this.$store.dispatch("ui/showLoading")
      if (values.password && values["re-password"] && values["old-password"]) {
        AuthService.changePassword(values["old-password"], values.password, values["re-password"])
          .then(() => {
            this.$store.dispatch("ui/hideLoading")
            //change tab
            this.$emit("changeSettingsTab", "Profile")
            this.$store.dispatch("ui/hideLoading")
            this.display_done_form = true
            this.$toast.success(this.$t("Password updated successfully"))
          })
          .catch(() => {
            this.$store.dispatch("ui/hideLoading")
            this.display_error_form = true
            this.$toast.error(this.$t("Wrong Password, please check old password and try again"))
          })
      }
    }
  }
}
</script>
